import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getPublicProducts } from '../redux/user/landingpageproducts/landingpage_service_syn';
import { FaEye, FaHeart, FaShoppingCart, FaArrowRight, FaBoxOpen } from 'react-icons/fa';
import { InfinitySpin } from 'react-loader-spinner';
import { updateCartIndicator } from '../redux/user/cart/cartSlices';
import { UserMajorCartCreation, UserGetCart } from '../redux/user/cart/cartServicesSync';
import { updateWishListIndicator } from '../redux/user/wish_list/wish_list_slices';
import { UserGetWishList, UserMajorWishListCreation } from '../redux/user/wish_list/wish_list_services_sync';
import LoaderScreen from '../component/loader/loaderScreen';

const CategoryPage = () => {
  const { categoryName } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [hoveredItem, setHoveredItem] = useState(null);

  const { products, loading, cartIndicator, wish_list } = useSelector((state) => ({
    products: state.landpage.products,
    loading: state.landpage.loading,
    cartIndicator: state.user_cart.cartIndicator,
    wish_list: state.user_wish_list.wish_list,
  }));

  useEffect(() => {
    dispatch(
      getPublicProducts({
        name: '""',
        category: categoryName,
        from: 0,
        to: 0,
        limit: 10,
      })
    );

    dispatch(UserGetCart());
    dispatch(UserGetWishList());
  }, [dispatch, categoryName]);

  const handleMouseEnter = (itemId) => {
    setHoveredItem(itemId);
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const handleAddToCart = (item) => {
    dispatch(updateCartIndicator(item._id));

    dispatch(
      UserMajorCartCreation({
        productId: item._id,
        quantity: 1,
      })
    )
      .then(() => {
        dispatch(UserGetCart());
        dispatch(updateCartIndicator(''));
      })
      .catch(() => {
        dispatch(updateCartIndicator(''));
      });
  };

  const handleViewProduct = (item) => {
    navigate(`/product/${item._id}`, { state: { product: item } });
  };

  const handleAddToWishlist = (item) => {
    dispatch(updateWishListIndicator(item._id));

    dispatch(
      UserMajorWishListCreation({
        productId: item._id,
        quantity: 1,
        price: item.price,
      })
    )
      .then(() => {
        dispatch(UserGetWishList());
        dispatch(updateWishListIndicator(''));
      })
      .catch(() => {
        dispatch(updateWishListIndicator(''));
      });
  };

  const isInWishlist = (itemId) => {
    return wish_list?.products?.some((item) => item.product === itemId);
  };

  const formatPrice = (value) => Number(value).toLocaleString('en-NG', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return (
    <main className="category-page">
      <div className="container-fluid">
        <nav className="category-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link><span aria-hidden="true">/</span>
          <Link to="/shop">Shop</Link><span aria-hidden="true">/</span>
          <span aria-current="page">{categoryName}</span>
        </nav>

        <header className="category-intro">
          <div>
            <span className="store-eyebrow">THE GLORIOUS EVIDENCE COLLECTION</span>
            <h1>{categoryName}</h1>
            <p>Find the right pieces for your space. Explore our {categoryName} collection.</p>
          </div>
          <Link to="/shop" className="category-browse">Browse all products <FaArrowRight aria-hidden="true" /></Link>
        </header>

        <section className="category-results" aria-label={`${categoryName} products`} aria-busy={loading}>
          <div className="category-toolbar">
            <div><h2>Explore the collection</h2><p aria-live="polite">{loading ? 'Finding your products…' : `${products?.docs?.length || 0} ${(products?.docs?.length === 1) ? 'product' : 'products'} shown`}</p></div>
            <span className="category-collection-tag">{categoryName}</span>
          </div>
          <div className="store-product-grid store-product-grid--catalog">
            {loading ? (
              <div className="category-state" role="status"><LoaderScreen height="140px" /><p>Loading the collection…</p></div>
            ) : products?.docs?.length > 0 ? (
              products.docs.map((item) => (
                <article
                  key={item._id}
                  className="store-catalog-card category-product"
                  onMouseEnter={() => handleMouseEnter(item._id)}
                  onMouseLeave={handleMouseLeave}
                >
                  {typeof item.tags === 'string' && item.tags.toLowerCase().includes('new') && (
                    <span className="category-product-badge">New</span>
                  )}
                  <Link to={`/product/${item._id}`} state={{ product: item }} className="store-card-image" aria-label={`View ${item.product_name}`}>
                    {item.gallery?.[0]?.pic_url ? (
                      <img
                        src={item.gallery?.[hoveredItem === item._id ? 1 : 0]?.pic_url || item.gallery[0].pic_url}
                        alt={item.product_name}
                        width={300}
                        height={300}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : <div className="category-image-placeholder"><FaBoxOpen aria-hidden="true" /><span>Image coming soon</span></div>}
                  </Link>
                  <div className="store-card-actions">
                    <button type="button" onClick={() => handleViewProduct(item)} aria-label={`View ${item.product_name}`}><FaEye /></button>
                    <button type="button" onClick={() => handleAddToWishlist(item)} aria-label={`Save ${item.product_name} to wishlist`} aria-pressed={!!isInWishlist(item._id)}>
                      <FaHeart className={isInWishlist(item._id) ? 'text-red-500' : 'text-gray-400'} />
                    </button>
                  </div>
                  <div className="store-card-content">
                    <h3><Link to={`/product/${item._id}`} state={{ product: item }} title={item.product_name}>{item.product_name}</Link></h3>
                    <div className="category-product-price">
                      <span>{item.symbol}{formatPrice(item.price)}</span>
                      {!!item.discount && <del>{item.symbol}{formatPrice(item.discount)}</del>}
                    </div>
                  </div>
                  <div className="category-product-footer">
                    <button type="button" onClick={() => handleAddToCart(item)} disabled={item._id === cartIndicator} aria-label={`Add ${item.product_name} to cart`}>
                      {item._id === cartIndicator ? <><InfinitySpin width="60" color="#FFFFFF" /><span className="visually-hidden">Adding to cart</span></> : <><FaShoppingCart aria-hidden="true" />Add to cart</>}
                    </button>
                  </div>
                </article>
              ))
            ) : (
              <div className="category-state category-empty">
                <FaBoxOpen aria-hidden="true" />
                <h3>This collection is being refreshed</h3>
                <p>There are no products to show in {categoryName} right now. Explore our other collections to find your next essential.</p>
                <Link to="/shop" className="primary__btn">Explore the shop <FaArrowRight aria-hidden="true" /></Link>
              </div>
            )}
          </div>
        </section>
        <aside className="category-help"><div><strong>Choosing equipment for your space?</strong><p>Our team is here to help you find the right fit.</p></div><Link to="/contact_us">Talk to us <FaArrowRight aria-hidden="true" /></Link></aside>
      </div>
    </main>
  );
};

export default CategoryPage;
